import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  CardActions,
  CardContent,
  CardMedia,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import { getLoginAnnouncements } from '../services/announcementService';

function LoginAnnouncementsDialog({ open, onClose }) {
  const [announcements, setAnnouncements] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let isMounted = true;

    async function loadAnnouncements() {
      setIsLoading(true);
      setError('');

      try {
        const data = await getLoginAnnouncements();
        if (isMounted) {
          setAnnouncements(data);
        }
      } catch (err) {
        console.error('Error cargando anuncios:', err);
        if (isMounted) {
          setAnnouncements([]);
          setError('No se pudieron cargar los anuncios.');
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    }

    if (open) {
      loadAnnouncements();
    }

    return () => {
      isMounted = false;
    };
  }, [open]);

  const handleOpenPdf = (pdfUrl) => {
    window.open(pdfUrl, '_blank', 'noopener,noreferrer');
  };

  const handleOpenYoutube = (youtubeUrl) => {
    window.open(youtubeUrl, '_blank', 'noopener,noreferrer');
  };

  const handleOpenWebsite = (websiteUrl) => {
    window.open(websiteUrl, '_blank', 'noopener,noreferrer');
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>Anuncios importantes</DialogTitle>
      <DialogContent dividers>
        {isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {!isLoading && error && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {!isLoading && !error && announcements.length === 0 && (
          <Typography color="text.secondary">
            No hay anuncios vigentes por el momento.
          </Typography>
        )}

        {!isLoading && announcements.length > 0 && (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, minmax(0, 1fr))',
                md: 'repeat(3, minmax(0, 1fr))',
              },
              gap: 2,
            }}
          >
            {announcements.map((announcement) => (
              <Box key={announcement.id}>
                <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <CardActionArea
                    aria-label={`Abrir PDF: ${announcement.title}`}
                    onClick={() => handleOpenPdf(announcement.pdfUrl)}
                    sx={{ flexGrow: 1, alignItems: 'stretch' }}
                  >
                    <CardMedia
                      component="img"
                      height="160"
                      image={announcement.thumbnailUrl}
                      alt={announcement.title}
                      sx={{ objectFit: 'cover' }}
                    />
                    <CardContent>
                      <Typography variant="subtitle1" component="h3" sx={{ fontWeight: 700 }}>
                        {announcement.title}
                      </Typography>
                      {announcement.description && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                          {announcement.description}
                        </Typography>
                      )}
                    </CardContent>
                  </CardActionArea>
                  {(announcement.youtubeUrl || announcement.websiteUrl) && (
                    <CardActions sx={{ px: 2, pb: 2, pt: 0, mt: 'auto', flexWrap: 'wrap', gap: 1 }}>
                      {announcement.youtubeUrl && (
                        <Button
                          size="small"
                          color="secondary"
                          variant="outlined"
                          aria-label={`Ver video: ${announcement.title}`}
                          onClick={() => handleOpenYoutube(announcement.youtubeUrl)}
                        >
                          Ver video
                        </Button>
                      )}
                      {announcement.websiteUrl && (
                        <Button
                          size="small"
                          color="primary"
                          variant="outlined"
                          aria-label={`Ir al sitio: ${announcement.title}`}
                          onClick={() => handleOpenWebsite(announcement.websiteUrl)}
                        >
                          Ir al sitio
                        </Button>
                      )}
                    </CardActions>
                  )}
                </Card>
              </Box>
            ))}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} aria-label="Cerrar anuncios">
          Cerrar
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default LoginAnnouncementsDialog;
